import PageRuleAPIHandler from "../../../Common/PageRules/PageRuleAPIHandler";

// NOTE: This file is intentionally in JavaScript as it gets injected into the sandbox frame

/* eslint-disable */
export function startSandbox(onlyAPI = false) {
    class PageRuleAPI {
        constructor() {
            this._parentWindow = false;
            this._variables    = new Map();
            this._waitingQueries  = [];
            this._waitingMessages = new Map();
        }

        _injectParent(parent) {
            this._parentWindow = parent;

            for(let i = 0, l = this._waitingQueries.length; i < l; i++) {
                this._waitingQueries[i]();
            }
        }

        _postToParent(
            type,
            data,
            callback
        ) {
            if(typeof data === "object") {
                if(typeof data.type !== "undefined") {
                    throw new Error("`type` is a reserved term");
                }

                if(typeof data.id !== "undefined") {
                    throw new Error("`id` is a reserved term");
                }
            } else {
                data = {};
            }

            let timeout = false;
            const id = Date.now();

            const query = () => {
                if(timeout) {
                    clearTimeout(timeout);
                }

                (this._parentWindow).postMessage({
                    type: "__sandbox_api_request",
                    data: {
                        type: type,
                        id,
                        ...data,
                    },
                }, "*");
            };

            if(callback) {
                this._waitingMessages.set(id, callback);
            }

            if(!this._parentWindow) {
                timeout = setTimeout(() => {
                    if(callback) {
                        callback("Parent window did not get injected quickly enough");
                        this._waitingMessages.delete(id);
                    }
                }, 5000);

                this._waitingQueries.push(query);
            } else {
                query();
            }
        }

        _receiveMessage(data) {
            const id = data.id;

            if(!this._waitingMessages.has(id)) {
                console.error("Could not find waiting message", id);
                return; // This shouldn't happen
            }

            const handler = this._waitingMessages.get(id);
            if(typeof handler === "function") {
                handler(data.error === true ? data.message : null, data);
            }

            this._waitingMessages.delete(id);
        }

        sendInternalRequest(
            url,
            method,
            body
        ) {
            return new Promise((res, rej) => {
                let timeout = false;

                const handler = (error, data) => {
                    if(error) {
                        rej(error);
                        return;
                    }

                    res(data.data);
                };

                const query = () => {
                    if(timeout !== false) {
                        clearTimeout(timeout);
                    }

                    this._postToParent("request", {
                        url,
                        method,
                        body,
                    }, handler);
                };

                if(!this._parentWindow) {
                    timeout = setTimeout(() => {
                        rej("Parent window did not get injected quick enough");
                    }, 5000);

                    this._waitingQueries.push(query);
                } else {
                    query();
                }
            });
        }

        queryGraphQL(query, variables) {
            return this.sendInternalRequest("/graphql", "POST", {
                query: query,
                variables,
            })
                .then(response => {
                    if(typeof response.data.errors !== "undefined") {
                        throw response;
                    }

                    return response.data;
                });
        }

        storeVariable(name, data) {
            this._variables.set(name, data);
        }

        getVariable(name) {
            if(this.hasVariable(name)) {
                throw new TypeError("Cannot get a variable that does not exist");
            }

            return this._variables.get(name);
        }

        hasVariable(name) {
            return this._variables.has(name);
        }

        redirect(address) {
            this._postToParent("redirect", {
                address,
            });
        }

        setField(field, value) {
            this._postToParent("field", {
                field,
                value,
            });
        }

        back() {
            this._postToParent("back");
        }

        next() {
            this._postToParent("next");
        }

        finish() {
            this._postToParent("finish");
        }

        toast(toastType, toastMessage) {
            this._postToParent("toast", {
                toastType,
                toastMessage,
            });
        }

        findByField(
            database,
            field,
            value
        ) {
            return new Promise((res, rej) => {
                const handler = (error, data) => {
                    if(error) {
                        rej(error);
                        return;
                    }

                    res(data);
                };

                this._postToParent("findByField", {
                    database,
                    field,
                    value,
                }, handler);
            });
        }
    }

    let invalidEntry = false;

    if(typeof entry !== "function") {
        invalidEntry = true;
    }

    function deepFreeze(obj) {
        const names = Object.getOwnPropertyNames(obj);

        for(const name of names) {
            const value = obj[name];

            obj[name] = (
                value &&
                typeof value === "object" ?
                    deepFreeze(value) :
                    value
            );
        }

        return Object.freeze(obj);
    }

    const formatDataFromEntry = (data) => {
        for(let i = 0, l = data.elements.length; i < l; i++) {
            if(typeof data.elements[i].customElement !== "undefined") {
                if(typeof data.elements[i].customElement.onEvent === "function") {
                    data.elements[i].customElement.onEvent = data.elements[i].customElement.onEvent.toString();
                }
            }
        }

        return data;
    };

    const loadAPI = () => {
        (window).ShopfrontAPI = new PageRuleAPI();
    };

    if(onlyAPI) {
        loadAPI();
        return;
    }

    // Start listening for connections
    let parentWindow = false;
    window.addEventListener("message", async (e) => {
        if (!parentWindow) {
            parentWindow = e.source;

            if ((window).ShopfrontAPI) {
                (window).ShopfrontAPI._injectParent(parentWindow);
            }
        }

        if (e.data === "__sandbox_initiate") {
            parentWindow.postMessage("__sandbox_initiated", "*");
        } else if (typeof e.data.type !== "undefined" && e.data.type === "__sandbox_execute") {
            if (invalidEntry) {
                parentWindow.postMessage({
                    type  : "__sandbox_error",
                    error : "Invalid page rule",
                    reason: "No entry point is specified",
                }, "*");

                return;
            }

            parentWindow.postMessage("__sandbox_executing", "*");

            let data;
            try {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                data = formatDataFromEntry(await entry(deepFreeze(e.data.context)));
            } catch (e) {
                try {
                    parentWindow.postMessage({
                        type  : "__sandbox_error",
                        error : e,
                        reason: "Could not execute page rule",
                    }, "*");
                } catch (err) {
                    parentWindow.postMessage({
                        type  : "__sandbox_error",
                        error : "Unknown error (unable to pass error to parent)",
                        reason: "Could not execute page rule",
                    }, "*");

                    console.error(
                        "Could not pass page rule error to parent window, errors are as follows (original, passing)",
                        e,
                        err
                    );
                }

                return;
            }

            parentWindow.postMessage({
                type: "__sandbox_result",
                data,
            }, "*");
        } else if (typeof e.data.type !== "undefined" && e.data.type === "__sandbox_api_reply") {
            if(typeof (window).ShopfrontAPI !== "undefined") {
                (window).ShopfrontAPI._receiveMessage(e.data.data);
            }
        }
    });

    if(!invalidEntry) {
        loadAPI();
    }
}

export default class PageRuleScript {
    script;
    frame;
    listening;
    awaitingListen;
    awaitingResult;
    invalidScript;
    noScript;
    finished;
    timeout;

    constructor(script) {
        this.script         = script;
        this.frame          = false;
        this.listening      = false;
        this.awaitingListen = false;
        this.awaitingResult = false;
        this.invalidScript  = false;
        this.noScript       = false;
        this.finished       = false;
        this.timeout        = false;

        this.frameListen   = this.frameListen.bind(this);
        this.handleMessage = this.handleMessage.bind(this);

        this.setup();

        startSandbox(true);
        (window).ShopfrontAPI._injectParent(window);
    }

    setup() {
        if (!this.script.trim()) {
            this.noScript = true;
        }

        try {
            let sandbox = "<script>";
            sandbox    += "\"use strict\"";
            sandbox    += "\n\n";
            sandbox    += this.script;
            sandbox    += "\n\n";
            sandbox    += startSandbox.toString();
            sandbox    += "\n\n";
            sandbox    += "startSandbox();";
            sandbox    += "</script>";

            const frame   = document.createElement("iframe");
            frame.srcdoc  = sandbox;
            frame.sandbox.add("allow-scripts");
            frame.classList.add("page-rule-sandbox");

            this.frame = frame;

            frame.addEventListener("load", this.frameListen);
            window.addEventListener("message", this.handleMessage);

            document.body.appendChild(this.frame);
        } catch(e) {
            console.error("Received an error in Page Rule:", e);
            this.invalidScript = true;

            if(this.awaitingResult) {
                this.awaitingResult("Invalid Script");
            }
        }
    }

    cleanup() {
        window.removeEventListener("message", this.handleMessage);
        if(this.frame) {
            document.body.removeChild(this.frame);
        }
    }

    frameListen() {
        if(!this.frame || !this.frame.contentWindow) {
            return;
        }

        this.frame.contentWindow.postMessage("__sandbox_initiate", "*");
    }

    formatResult(data) {
        for(let i = 0, l = data.elements.length; i < l; i++) {
            if(typeof data.elements[i].customElement !== "undefined") {
                if(typeof data.elements[i].customElement.onEvent === "string") {
                    data.elements[i].customElement.onEvent = eval(
                        data.elements[i].customElement.onEvent.toString()
                    ); // TODO: Find a different way to do this
                }
            }
        }

        return data;
    }

    handleMessage(e) {
        if(this.timeout) {
            clearTimeout(this.timeout);
        }

        if (this.finished) {
            return;
        }

        if(e.data === "__sandbox_initiated") {
            this.listening = true;

            if(this.awaitingListen) {
                this.awaitingListen();
            }
        } else if(typeof e.data.type === "string") {
            if(e.data.type === "__sandbox_error") {
                if(this.awaitingResult) {
                    this.awaitingResult(e.data);
                }
            } else if(e.data.type === "__sandbox_result") {
                const data = this.formatResult(e.data.data);

                if(this.awaitingResult) {
                    this.awaitingResult(null, data);
                }
            } else if(e.data.type === "__sandbox_api_request") {
                const data = e.data.data;

                if (data.type === "finish") {
                    this.finished = true;
                }

                PageRuleAPIHandler.handle(data, (data, target) => {
                    if(!this.frame) {
                        return;
                    }

                    this.frame.contentWindow?.postMessage(data, target);
                });
            }
        }

        // Ignore other data as it's probably not intended for this function
    }

    run(context) {
        if(this.noScript) {
            return Promise.reject(false);
        }

        if(this.invalidScript) {
            return Promise.reject("Script is invalid");
        }

        const promise = new Promise((res, rej) => {
            this.awaitingResult = (error, result) => {
                if(error) {
                    rej(error);
                    return;
                }

                res(result);

                this.awaitingResult = false;
            };
        });

        const execute = () => {
            if(!this.frame) {
                return;
            }

            this.frame.contentWindow?.postMessage({
                type: "__sandbox_execute",
                context,
            }, "*");

            if(this.timeout) {
                clearTimeout(this.timeout);
            }

            this.timeout = setTimeout(() => {
                if(this.awaitingResult) {
                    this.awaitingResult("Execution timed out");
                }
            }, 500);
        };

        if(!this.listening) {
            this.awaitingListen = execute;

            this.timeout = setTimeout(() => {
                if(this.awaitingResult) {
                    this.awaitingResult("Listening timed out");
                }
            }, 500);
        } else {
            execute();
        }

        return promise;
    }
}
/* eslint-enable */
